import React from 'react';
import { useTranslatedContent } from '../../i18n/hooks/useTranslatedContent';
import { BarChart, Database, Users, ShoppingCart, FileText, Briefcase, Settings, Workflow } from 'lucide-react';
import Button from '../common/Button';

const ERPSection = () => {
  const { t } = useTranslatedContent('erp');

  return (
    <div id="erp" className="relative py-24 overflow-hidden">
      {/* Modern gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-violet-900 to-fuchsia-900">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'30\' height=\'30\' viewBox=\'0 0 30 30\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Ccircle cx=\'1.22676\' cy=\'1.22676\' r=\'1.22676\' fill=\'rgba(255,255,255,0.1)\'/%3E%3C/svg%3E')] opacity-20" />
      </div>

      {/* Animated orbs */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-purple-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob" />
        <div className="absolute top-1/3 -right-48 w-96 h-96 bg-violet-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute -bottom-48 left-1/2 w-96 h-96 bg-fuchsia-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-purple-500/10 border border-purple-400/20 backdrop-blur-sm mb-8">
            <BarChart className="h-5 w-5 text-purple-400 mr-2" />
            <span className="text-purple-200 font-medium">Enterprise Business Solutions</span>
          </div>
          
          <h2 className="text-4xl font-bold text-white mb-6">{t('title')}</h2>
          <p className="text-xl text-purple-100 max-w-3xl mx-auto">
            {t('subtitle')}
          </p>
        </div>

        {/* Solutions Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
          {/* Dolibarr */}
          <div className="group relative">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-violet-500 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-500"></div>
            <div className="relative bg-black/50 backdrop-blur-sm rounded-xl p-8">
              <div className="flex items-center mb-6">
                <Database className="h-8 w-8 text-purple-400 mr-3" />
                <h3 className="text-xl font-semibold text-white">Dolibarr ERP & CRM</h3>
              </div>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="p-2 bg-purple-500/20 rounded-lg mr-4">
                    <Users className="h-5 w-5 text-purple-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-white mb-2">Customer Management</h4>
                    <p className="text-purple-100">Complete CRM with customer tracking and opportunity management</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="p-2 bg-purple-500/20 rounded-lg mr-4">
                    <ShoppingCart className="h-5 w-5 text-purple-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-white mb-2">Sales & Purchases</h4>
                    <p className="text-purple-100">Streamlined order processing and inventory management</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Odoo */}
          <div className="group relative">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-500"></div>
            <div className="relative bg-black/50 backdrop-blur-sm rounded-xl p-8">
              <div className="flex items-center mb-6">
                <Briefcase className="h-8 w-8 text-violet-400 mr-3" />
                <h3 className="text-xl font-semibold text-white">Odoo Enterprise</h3>
              </div>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="p-2 bg-violet-500/20 rounded-lg mr-4">
                    <Settings className="h-5 w-5 text-violet-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-white mb-2">All-in-One Business Platform</h4>
                    <p className="text-purple-100">Integrated modules for every business need</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="p-2 bg-violet-500/20 rounded-lg mr-4">
                    <Workflow className="h-5 w-5 text-violet-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-white mb-2">Automation & Workflows</h4>
                    <p className="text-purple-100">Automated business processes and custom workflows</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {[
            {
              icon: Users,
              title: 'Customer Management',
              description: 'Complete CRM functionality with lead tracking and opportunity management'
            },
            {
              icon: ShoppingCart,
              title: 'Sales & Inventory',
              description: 'Streamlined sales process and inventory control'
            },
            {
              icon: FileText,
              title: 'Accounting & Finance',
              description: 'Integrated accounting with real-time financial reporting'
            },
            {
              icon: Settings,
              title: 'Business Automation',
              description: 'Automated workflows and business process management'
            }
          ].map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div key={index} className="group relative bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-gradient-to-br from-purple-500 to-violet-500 rounded-lg">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-purple-100">{feature.description}</p>

                {/* Decorative elements */}
                <div className="absolute top-4 right-4 w-2 h-2 bg-purple-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-4 left-4 w-2 h-2 bg-violet-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            );
          })}
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <Button
            variant="secondary"
            className="bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white border-0"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            {t('cta')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ERPSection;